import type { CSSProperties } from 'react';

/**
 * لوحة الـ 12 لونًا المغلقة (§5.3) — لا color picker حر. `venture.color` يحمل
 * الاسم والرمز هنا يترجمه إلى متغير الرمز المعرَّف في `styles/tokens.css`.
 *
 * **قيد:** الأسماء الاثنا عشر هي القيم القانونية لعمود `venture.color` — والقاعدة
 * تفرضها بقيد `venture_color_ck` منذ الهجرة 0010، ومخطط الإدخال `z.enum` (بند N-12).
 * قبلها كان الوصف كاذبًا: افتراض العمود نفسه كان `zinc` وهو خارج اللوحة.
 *
 * وأي اسم خارجها يسقط على `slate` بهدوء بدل أن يفقد المظلة هويتها — والسقوط الآن
 * احتياطٌ لبياناتٍ قديمة لا مسارٌ عاديّ.
 *
 * **وهذه النسخة الوحيدة في الويب**: كانت ثلاثًا (هنا، و`app/shell.tsx`،
 * و`features/capture/project-options.ts`) متطابقة اليوم ولا شيء يمنع انحرافها غدًا،
 * و`check:verb-mirror` يحرسها الآن مقابل `VENTURE_COLORS` في النطاق.
 */
export const VENTURE_COLOR_VARS: Readonly<Record<string, string>> = {
  garnet: 'var(--color-venture-garnet)',
  coral: 'var(--color-venture-coral)',
  amber: 'var(--color-venture-amber)',
  olive: 'var(--color-venture-olive)',
  oasis: 'var(--color-venture-oasis)',
  teal: 'var(--color-venture-teal)',
  sky: 'var(--color-venture-sky)',
  indigo: 'var(--color-venture-indigo)',
  violet: 'var(--color-venture-violet)',
  plum: 'var(--color-venture-plum)',
  rose: 'var(--color-venture-rose)',
  slate: 'var(--color-venture-slate)',
};

const FALLBACK = 'var(--color-venture-slate)';

export function ventureColorVar(color: string | null | undefined): string {
  if (color === null || color === undefined) return FALLBACK;
  return VENTURE_COLOR_VARS[color] ?? FALLBACK;
}

/**
 * §5.3: اللون لا يُستخدم أبدًا خلفية مساحة كبيرة — نقطة، أو شريط 3px على الحافة
 * البادئة، أو خلفية شارة بمزج 14% مع سطح البطاقة. هذه الدوال الثلاث هي الاستخدامات
 * المسموحة كلها، ولا يُكتب لون مظلة في أي مكان آخر.
 */
export function ventureDotStyle(color: string | null | undefined): CSSProperties {
  return { backgroundColor: ventureColorVar(color) };
}

/** شريط الهوية على الحافة البادئة للبطاقة — 3px بالمواصفة حرفيًا. */
export function ventureEdgeStyle(color: string | null | undefined): CSSProperties {
  return { borderInlineStartWidth: '3px', borderInlineStartColor: ventureColorVar(color) };
}

/**
 * خلفية الشارة. نص الشارة يبقى `--color-text` (لا لون المظلة): بذلك يتحقق تباين
 * AA في الوضعين بلا مزج نص مشروط بالسمة، ويظل اللون هوية لا صخبًا.
 */
export function ventureBadgeStyle(color: string | null | undefined): CSSProperties {
  return {
    backgroundColor: `color-mix(in oklch, ${ventureColorVar(color)} 14%, var(--color-surface))`,
  };
}
